import InputField from "components/form/InputField";
import LoaderRing from "components/ui-component/LoaderRing";
import { getCountryCallingCode } from "react-phone-number-input";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import { RootState } from "store/store";
import { valuesProps } from "types/paymentMethods";
import { CountryCode } from "types/UserProfile";
import {
  checkPhone,
  formatCurrency,
  formatMinAndMaxAmount,
  getBankLogo,
  getCountryShortName,
  validateEmail,
} from "utils/HelperFunctions";
import { PhoneInput } from "types/common";
import MobileInputNew from "components/form/MobileInputNew";
import Button from "components/form/Button";
import { ConvenienceFeeDisplay } from "./PaymentMethodComponents";
import { SettingsValue } from "types/payments";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { useTid } from "utils/testid";
import { getPaymentTotalWithFee } from "utils/paymentFee";

declare global {
  interface Window {
    StaxJs: any;
  }
}

interface BankFormProps {
  handleChange: (e: any, value: string) => void;
  values: valuesProps;
  numberNotmatchedError: boolean;
  bankName: string;
  errMsg: string | undefined;
  isRingLoading: boolean;
  disableInputFields?: boolean;
  error?: string | undefined;
  setnumberNotmatchedError: (val: boolean) => void;
  collectPhone?: boolean;
  phoneNumber?: PhoneInput;
  setPhoneNumber?: React.Dispatch<React.SetStateAction<PhoneInput>>;
  email: string;
  setEmail: (value: string) => void;
  verifyStaxCardBank?: ({
    cc_token,
    cc_token2,
    x_method,
  }: {
    cc_token?: string;
    cc_token2?: string;
    x_method?: string;
  }) => Promise<void>;
  amount?: any;
  isEdit?: boolean;
  isPaymentLink?: boolean;
  isSecretLoading?: boolean;
  isRecurring?: boolean;
  paymentPlan?: any;
  isRetry?: boolean;
  setIsDisable?: any;
  isAgreement?: boolean;
  handleBack?: any;
  isUpdatePlan?: boolean;
  setShowPreview?: any;
  isSchedulePayment?: boolean;
  isProductLink?: boolean;
  isUpdateSubscription?: boolean;
  checkNegativeBalance?: boolean;
  accounts_id?: string | null;
  isUpdateSubscriptionPaymentMethod?: boolean;
  showConvFee?: boolean;
  isDowngradePlan?: boolean;
  handleEmailBlur?: () => void;
}

const StaxBankForm = ({
  handleChange,
  values,
  numberNotmatchedError,
  bankName,
  errMsg,
  isRingLoading,
  disableInputFields = false,
  error = undefined,
  collectPhone = false,
  setnumberNotmatchedError,
  phoneNumber = { phone: "", country_code: "", postal_code: "" },
  email = "",
  setEmail = () => {},
  setPhoneNumber = () => {},
  amount = 0,
  isEdit = false,
  isPaymentLink = true,
  isSecretLoading = false,
  isRecurring = false,
  isUpdateSubscriptionPaymentMethod = false,
  paymentPlan,
  isRetry = false,
  setIsDisable = () => {},
  isAgreement = false,
  handleBack = () => {},
  isUpdatePlan = false,
  setShowPreview = () => {},
  isSchedulePayment = false,
  isProductLink = false,
  isUpdateSubscription = false,
  checkNegativeBalance = false,
  accounts_id = "",
  verifyStaxCardBank = async () => {},
  handleEmailBlur = () => {},
  showConvFee = false,
  isDowngradePlan = false,
}: BankFormProps) => {
  const tid = useTid();
  const [logo, setLogo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const userData = useUserData();
  const [stax, setStax] = useState<any>(null);
  const { paymentPermission, generalSettings, conFee, isFeeLoading } =
    useAppSelector((state: RootState) => state.payment);

  const { isChecking, isSubscriptionAllowed } = useAppSelector(
    (state: RootState) => state.subscription
  );

  const portalSettings = usePortalSettingsData();

  const scriptLoaded = useRef<boolean>(false);

  const initStax = () => {
    const token = paymentPermission?.ach_gateway_name?.key;
    const config = {};
    const instance = new window.StaxJs(token, config);

    if (!instance) {
      return;
    }
    setStax(instance);
  };

  useEffect(() => {
    if (scriptLoaded.current) return;

    const script = document.createElement("script");
    script.src = "https://staxjs.staxpayments.com/staxjs-captcha.js";
    script.async = true;
    script.onload = () => initStax();

    document.body.appendChild(script);
    scriptLoaded.current = true;

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (bankName) {
      let logo = getBankLogo({ bankName, type: "small" });
      setLogo(logo || null);
    }
  }, [bankName]);

  useEffect(() => {
    if (userData?.data?.accountData?.email) {
      setEmail(userData?.data?.accountData?.email || "");
    }
  }, [userData]);

  const isPaymentDisable = useMemo(() => {
    const isPhone = collectPhone ? checkPhone(phoneNumber) : true;
    return (
      isEdit ||
      !values?.choldername ||
      !values?.choldername.trim() ||
      !isPhone ||
      !email ||
      !email.trim() ||
      !validateEmail(email) ||
      !values?.account_number ||
      !values?.routing_number ||
      values?.account_number !== values?.r_account_number
    );
  }, [values, isEdit, email, phoneNumber, collectPhone]);

  const amountLimit = useMemo(() => {
    if (
      userData?.data?.accountData?.balance &&
      generalSettings?.systemSettings
    ) {
      const { min, max } = formatMinAndMaxAmount(
        generalSettings?.systemSettings,
        userData?.data?.accountData?.balance
      );

      return { min, max };
    }

    return { min: 1.0, max: 100000.0 };
  }, [generalSettings, userData]);

  let amountCheck = useMemo(() => {
    if (isDowngradePlan || isUpdateSubscriptionPaymentMethod) {
      return false;
    }
    return amount < amountLimit.min || amount > amountLimit.max;
  }, [amount, amountLimit, isDowngradePlan, isUpdateSubscriptionPaymentMethod]);

  const handlePhoneCountryChange = useCallback((itm: string) => {
    setPhoneNumber((state) => ({
      ...state,
      country_code: getCountryCallingCode(itm as CountryCode),
    }));
  }, []);

  const handleOnPhoneChange = useCallback((phone: string) => {
    setPhoneNumber((state) => ({
      ...state,
      phone,
    }));
  }, []);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      let payload: any = {
        method: "bank",
        bank_name: values?.bank_name || bankName,
        bank_account: values.account_number,
        bank_routing: values.routing_number,
        firstname: userData?.data?.accountData?.firstName,
        lastname: userData?.data?.accountData?.lastName,
        person_name: values.choldername || "",
        validate: false,
        bank_type: "checking",
        bank_holder_type: "personal",
        email: email || userData?.data?.accountData?.email,
      };

      if (collectPhone) {
        payload = { ...payload, phone: phoneNumber?.phone };
      } else if (userData?.data?.accountData?.phone) {
        payload["phone"] = userData?.data?.accountData?.phone || "";
      }

      if ((userData?.data?.accountData?.country || "USA")?.length > 3) {
        payload = {
          ...payload,
          address_country: userData?.data?.accountData?.country ?? "USA",
        };
      }
      if (userData?.data?.accountData?.city?.length > 0) {
        payload = {
          ...payload,
          address_city: userData?.data?.accountData?.city ?? "",
        };
      }
      if (userData?.data?.accountData?.postal_code?.length > 0) {
        payload = {
          ...payload,
          address_zip: userData?.data?.accountData?.postal_code ?? "",
        };
      }
      if (userData?.data?.accountData?.state?.length > 0) {
        payload = {
          ...payload,
          address_state: userData?.data?.accountData?.state ?? "",
        };
      }
      if (userData?.data?.accountData?.address1?.length > 0) {
        payload = {
          ...payload,
          address_1: userData?.data?.accountData?.address1 ?? "",
        };
      }
      if (userData?.data?.accountData?.address2?.length > 0) {
        payload = {
          ...payload,
          address_2: userData?.data?.accountData?.address2 ?? "",
        };
      }

      const res = await stax.tokenize(payload);
      const cc_token = res?.id;
      const cc_token2 = res?.customer_id;
      const response = await verifyStaxCardBank({
        cc_token,
        cc_token2,
        x_method: "ACH",
      });
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div data-testid={tid("stax-bank-form")} aria-label="Stax bank form">
      <div
        className="flex flex-col w-full lg:gap-[30px] gap-5"
        data-testid={tid("stax-bank-fields")}
      >
        <div className="w-full">
          <InputField
            name="choldername"
            label="Account holder name"
            className="!py-[10px] !rounded-[7px]"
            _className="!text-xs !font-bold !leading-normal"
            placeholder="Full name of account holder"
            value={values?.choldername}
            onChange={(e) => handleChange(e, "choldername")}
            disabled={disableInputFields}
            data-testid={tid("stax-bank-account-holder-input")}
            aria-label="Bank account holder name"
          />
        </div>
        <div className="w-full">
          <InputField
            type="number"
            name="account_number"
            label="Account Number"
            className={`!py-[10px] !rounded-[7px] ${
              numberNotmatchedError || error ? "!border-red" : ""
            }`}
            _className="!text-xs !font-bold !leading-normal"
            placeholder="Enter Account Number"
            value={values?.account_number}
            onChange={(e) => {
              handleChange(e, "account_number");
              setnumberNotmatchedError(false);
            }}
            disabled={disableInputFields}
            data-testid={tid("stax-bank-account-number-input")}
            aria-label="Bank account number"
          />
        </div>
        <div className="w-full">
          <InputField
            type="number"
            name="r_account_number"
            label="Re-enter Account Number"
            className={`!py-[10px] !rounded-[7px] ${
              numberNotmatchedError || error ? "!border-red" : ""
            }`}
            _className="!text-xs !font-bold !leading-normal"
            placeholder="Re-enter Account Number"
            value={values?.r_account_number}
            onChange={(e) => {
              handleChange(e, "r_account_number");
              setnumberNotmatchedError(false);
            }}
            error={
              numberNotmatchedError
                ? "Account number are not matched"
                : undefined
            }
            onBlur={() => {
              if (values?.r_account_number !== values?.account_number) {
                setnumberNotmatchedError(true);
              }
            }}
            disabled={disableInputFields}
            data-testid={tid("stax-bank-confirm-account-number-input")}
            aria-label="Confirm bank account number"
          />
        </div>
        <div className="w-full relative">
          <InputField
            type="number"
            name="routing_number"
            label="Routing Number"
            className={`!py-[10px] !rounded-[7px] ${error ? "!border-red" : ""}`}
            _className="!text-xs !font-bold !leading-normal"
            placeholder="Enter Routing Number"
            value={values?.routing_number}
            onChange={(e) => handleChange(e, "routing_number")}
            error={errMsg}
            disabled={disableInputFields}
            data-testid={tid("stax-bank-routing-number-input")}
            aria-label="Bank routing number"
          />
          {bankName && logo && !isRingLoading && (
            <div className="absolute right-[15px] top-[50%]">{logo}</div>
          )}
          {!errMsg && bankName && (
            <div className="absolute top-[100%] left-0">
              <div
                className={`mt-[3px] text-xs leading-normal text-txtSecondary capitalize`}
              >
                {bankName?.toLowerCase()}
              </div>
            </div>
          )}
          {isRingLoading && (
            <div className="absolute right-[24px] top-[50%]">
              <div className="text-xs leading-[16px] text-gray1 ">
                <LoaderRing exsm />
              </div>
            </div>
          )}
        </div>

        {showConvFee &&
          portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
            <ConvenienceFeeDisplay
              conFee={conFee}
              isFeeLoading={isFeeLoading}
              isPaymentLink={isPaymentLink}
              setShowPreview={setShowPreview}
              isStaxGateway={true}
              isTokenAvailable={false}
              customClassName=""
            />
          )}
      </div>
      <div
        className={`w-full py-5 ${isUpdatePlan || isAgreement ? "mb-5" : ""}`}
      >
        <div className="text-xs font-bold leading-[18px] text-txtSecondary mb-2 mt-3">
          EMAIL
        </div>
        <div className="relative w-full ">
          <input
            autoComplete="off"
            data-testid={tid("stax-bank-email-input")}
            aria-label="Email address"
            className={`w-full border-strokePrimary rounded-[5px] hover:!border-strokeActive border py-2 px-[15px] bg-bgPrimary text-sm text-txtPrimary font-normal leading-normal placeholder:text-txtSecondary focus:outline-none ${
              userData?.data?.accountData?.email
                ? "!cursor-not-allowed	!bg-bgSecondary"
                : "cursor-text"
            }`}
            placeholder="Email address"
            onBlur={handleEmailBlur}
            onChange={(e) => {
              setEmail(
                userData?.data?.accountData?.email
                  ? userData?.data?.accountData?.email
                  : e.target.value
              );
            }}
            value={
              userData?.data?.accountData?.email
                ? userData?.data?.accountData?.email
                : email
            }
            disabled={userData?.data?.accountData?.email}
          />
        </div>

        {collectPhone && (
          <div className="pt-5">
            <div
              className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
            >
              Phone
            </div>
            <MobileInputNew
              inputValue={phoneNumber?.phone || ""}
              isProductLinkInput={true}
              userCountry={
                getCountryShortName(phoneNumber?.country_code) || "US"
              }
              setCountry={handlePhoneCountryChange}
              setInputValue={handleOnPhoneChange}
              data-testid={tid("stax-bank-phone-input")}
              aria-label="Phone number"
            />
          </div>
        )}
      </div>

      {!isRetry &&
        (isAgreement ? (
          <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
            <Button
              text={`Back`}
              variant="white"
              onClick={handleBack}
              className="lg:w-40 w-full max-w-40"
              data-testid={tid("stax-bank-back-button")}
              aria-label="Go back from Stax bank payment"
            />
            <Button
              text={`Confirm`}
              isDisabled={amountCheck || isPaymentDisable || isFeeLoading}
              isLoading={isLoading || isSecretLoading}
              onClick={() => {
                handleSubmit();
              }}
              className="lg:w-40 w-full max-w-40"
              variant="variable"
              data-testid={tid("stax-bank-confirm-button")}
              aria-label="Confirm Stax bank payment"
            />
          </div>
        ) : isSchedulePayment ? (
          <div
            className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5"
          >
            <div
              className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
            >
              {isUpdatePlan && (
                <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
              )}

              <div className="w-full flex justify-between gap-5">
                <Button
                  className="lg:!w-40 !w-full"
                  isDisabled={isLoading}
                  text="Back"
                  type="button"
                  variant="secondary"
                  onClick={handleBack}
                  data-testid={tid("stax-bank-schedule-back-button")}
                  aria-label="Go back from scheduled Stax bank payment"
                />
                <Button
                  className="lg:!w-40 !w-full"
                  isLoading={isLoading}
                  isDisabled={amountCheck || isPaymentDisable || isFeeLoading}
                  text={isUpdatePlan ? "Update" : "Confirm"}
                  type="button"
                  onClick={() => {
                    handleSubmit();
                  }}
                  variant="variable"
                  data-testid={tid("stax-bank-schedule-confirm-button")}
                  aria-label={
                    isUpdatePlan
                      ? "Update scheduled Stax bank payment"
                      : "Confirm scheduled Stax bank payment"
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <Button
            text={
              isUpdatePlan || isUpdateSubscription
                ? "Update"
                : `Pay ${formatCurrency(getPaymentTotalWithFee(amount, conFee)) || 0.0}`
            }
            className={"!w-full mt-5"}
            isDisabled={
              amountCheck ||
              isPaymentDisable ||
              isFeeLoading ||
              (isProductLink && !isSubscriptionAllowed)
            }
            onClick={() => {
              handleSubmit();
            }}
            isLoading={
              isLoading || isSecretLoading || isFeeLoading || isChecking
            }
            variant={"variable"}
            data-testid={tid("stax-bank-submit-button")}
            aria-label={
              isUpdatePlan || isUpdateSubscription
                ? "Update Stax bank payment"
                : "Pay with Stax bank"
            }
          />
        ))}
    </div>
  );
};

export default StaxBankForm;
